import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { isCenterViewTab, setItemWithExpiration } from './utils';
import { readSharedAuthValue } from './sharedAuthStorage';

/**
 * 늦게 도착한 응답이 다른 계정의 저장소를 덮지 않게 하는 관문.
 *
 * 문제(웹 검증 2026-11-02, 선재 P1): A 계정에서 보낸 요청의 응답이 로그아웃·B 로그인 뒤에 도착하면, 콜백이 잡아 둔
 * A 의 authorization·authObject 를 다시 저장해 **B 의 저장소가 A 토큰·A 센터로 바뀌었다.** useMutation 의 onSuccess 는
 * 화면이 사라지고 캐시를 비운 뒤에도 실행된다.
 *
 * 원칙: 「비동기 작업을 시작한 쪽(소유 세션) = 실제 요청에 쓴 자격증명 = 응답을 저장할 대상」.
 * - 소유자는 **요청을 만드는 코드가 쓰는 바로 그 authObject·authorization**(화면의 상태·props)으로 잡는다. 저장소를
 *   mutate 시점에 다시 읽으면, 사용자 동작 → 비동기 준비(사진 압축 등) 사이에 계정이 바뀐 경우 B 를 소유자로 잡고
 *   A 자격증명으로 요청한 뒤 A 를 저장해 버린다(2회차 검증 반례).
 * - 같은 사람인지는 원시 토큰이 아니라 **계정 식별**(역할·관리자/슈퍼관리자 id·센터·열람 탭)로 본다. 같은 계정의 토큰
 *   갱신은 같은 세션으로 보고 콜백을 그대로 실행한다. 다른 계정이면 저장·알림 콜백을 건너뛴다(지역 화면 정리만).
 * - authorization 은 **지금 저장소 값이 요청 때 값과 같을 때만** 다시 쓴다 — 갱신된 새 토큰을 옛 토큰으로 되돌리지 않는다.
 * - 최신 저장소를 펼쳐 섞지 않는다(A 응답이 B 세션에 섞인다).
 * - 「지금」 값은 **탭이 함께 쓰는 저장소의 최신 값**(lib/sharedAuthStorage)으로 본다. 이 탭의 react-secure-storage
 *   메모리는 탭 시작 때 값이라, 다른 탭의 로그아웃·다른 계정 로그인·토큰 갱신을 보지 못한다(3회차 검증 반례 — 탭1 의
 *   늦은 응답이 탭2 의 B 세션·A2 토큰을 덮었다). 확인과 쓰기 사이에 await 를 두지 않는다.
 * 로그인·열람 모드 시작은 새 세션을 만드는 곳이라 이 관문을 쓰지 않는다.
 */

/** 계정 식별 — 식별할 id 가 하나도 없으면 null(누구와도 같다고 보지 않는다, fail-closed). */
export const authAccountKeyOf = (authObject: any): string | null => {
    if (!authObject || typeof authObject !== 'object') return null;
    const adminId = authObject.adminId ?? '';
    const superAdminId = authObject.superAdminId ?? '';
    const userId = authObject.id ?? '';
    if (!adminId && !superAdminId && !userId) return null;
    return [
        isCenterViewTab() ? 'view' : 'main',
        authObject.role ?? 'admin',
        String(adminId),
        String(superAdminId),
        String(userId),
        String(authObject.centerId ?? ''),
    ].join('|');
};

export type AuthOwner = { key: string | null; token: string | null };

/** 탭이 함께 쓰는 저장소의 지금 토큰(없거나 만료면 null). */
const currentToken = (): string | null => {
    const token = readSharedAuthValue('authorization');
    return typeof token === 'string' && token ? token : null;
};

/** 탭이 함께 쓰는 저장소의 지금 계정 식별. */
export const currentAuthAccountKey = (): string | null => authAccountKeyOf(readSharedAuthValue('authObject'));

/**
 * 소유 세션을 잡는다. 요청에 쓰는 authObject·authorization 을 넘긴다(생략하면 지금 저장소 값 — 요청도 저장소를 쓰는
 * 곳에서만 생략한다).
 */
export const captureAuthSession = (authObject?: any, authorization?: string | null): AuthOwner => ({
    key: authObject === undefined ? currentAuthAccountKey() : authAccountKeyOf(authObject),
    token: authorization === undefined ? currentToken() : authorization ?? null,
});

/**
 * 지금(탭 공유) 저장소가 소유자와 같은 계정인가(로그아웃 상태면 아니다).
 *
 * 알려진 한계(수용 — 사용자 정책 「저확률 예외는 수용·기록」, 4회차 검증 1번): 이 확인과 바로 뒤의 저장은 한 탭 안에서는
 * 끊기지 않지만, **다른 탭과는 원자적이지 않다.** 확인을 통과한 직후(마이크로초 단위) 다른 탭이 로그아웃·다른 계정 로그인을
 * 끝내면 이 탭의 저장이 그 위에 덮인다. localStorage 는 탭 사이 잠금을 주지 않으므로(WHATWG Web Storage) 읽기를 더 해도
 * 닫히지 않고, 닫으려면 로그인·로그아웃·인증 저장을 모두 같은 Web Locks(navigator.locks) 아래로 모아야 한다.
 */
export const isAuthSessionCurrent = (owner: AuthOwner | null | undefined): boolean => {
    if (!owner?.key || !currentToken()) return false;
    return currentAuthAccountKey() === owner.key;
};

/**
 * (탭 사이 확인·저장 경합의 한계는 isAuthSessionCurrent 주석 참고 — Web Locks 로만 닫힌다.)
 * 콜백이 「요청 때 토큰」을 다시 저장하던 자리. 지금 저장소 토큰이 그 값과 같을 때만 (만료 시각을 늘려) 다시 쓴다.
 * 다르면(같은 계정에서 갱신됐거나 다른 계정) 아무것도 쓰지 않는다 — 새 토큰을 옛 토큰으로 되돌리지 않는다.
 */
export const keepAuthorizationIfUnchanged = (requestToken: string | null | undefined, expirationInHours = 12) => {
    const token = currentToken();
    if (token && requestToken && token === requestToken) setItemWithExpiration('authorization', token, expirationInHours);
};

type GuardedContext<TContext> = { authOwner: AuthOwner; inner: TContext | undefined };

export type AuthSessionMutationGuard = {
    /**
     * 요청을 만드는 코드가 쓰는 자격증명(화면의 authObject·authorization). onMutate 때 부른다 — 압축 같은 준비가 끝난 뒤
     * mutate 해도 요청에 실제로 쓰는 값과 같은 출처라 계정이 바뀐 사이를 놓치지 않는다.
     */
    owner: () => { authObject: any; authorization?: string | null };
    /** 다른 계정이라 콜백을 건너뛸 때 지역 화면만 정리한다(로딩 끄기 등). 저장소·알림은 건드리지 마라. */
    onSessionChanged?: () => void;
};

/**
 * useMutation 과 같지만, 요청을 시작한 계정이 지금 계정이 아니면 onSuccess·onError·onSettled 를 **부르지 않는다**
 * (대신 onSessionChanged 로 지역 화면만 정리). 같은 계정이면(토큰 갱신 포함) 그대로 부른다.
 */
export const useAuthSessionMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
    guard: AuthSessionMutationGuard,
) => {
    const skip = () => {
        guard.onSessionChanged?.();
        return undefined;
    };
    return useMutation<TData, TError, TVariables, GuardedContext<TContext>>({
        ...(options as any),
        onMutate: async (variables: TVariables) => {
            const { authObject, authorization } = guard.owner();
            const authOwner = captureAuthSession(authObject, authorization);
            const inner = options.onMutate ? await options.onMutate(variables) : undefined;
            return { authOwner, inner };
        },
        onSuccess: (data: TData, variables: TVariables, context: GuardedContext<TContext> | undefined) => {
            if (!isAuthSessionCurrent(context?.authOwner)) return skip();
            return options.onSuccess?.(data, variables, context?.inner as TContext);
        },
        onError: (error: TError, variables: TVariables, context: GuardedContext<TContext> | undefined) => {
            if (!isAuthSessionCurrent(context?.authOwner)) return skip();
            return options.onError?.(error, variables, context?.inner);
        },
        onSettled: (
            data: TData | undefined,
            error: TError | null,
            variables: TVariables,
            context: GuardedContext<TContext> | undefined,
        ) => {
            // 건너뛰는 경우 onSuccess/onError 에서 이미 정리했다.
            if (!isAuthSessionCurrent(context?.authOwner)) return undefined;
            return options.onSettled?.(data, error, variables, context?.inner);
        },
    });
};

/**
 * useQuery 처럼 콜백이 요청과 따로 불리는 곳 — 조회를 시작할 때 소유 세션을 잡아 결과에 붙인다.
 * onSuccess 에서 isResultFromCurrentSession(res) 로 확인한다.
 */
export const AUTH_SESSION_MARK_KEY = '__authSession';

export const withAuthSessionMark = async <T>(
    fetcher: () => Promise<T>,
    owner: { authObject: any; authorization?: string | null },
): Promise<T> => {
    const mark = captureAuthSession(owner.authObject, owner.authorization);
    const result = await fetcher();
    if (result && typeof result === 'object') (result as any)[AUTH_SESSION_MARK_KEY] = mark;
    return result;
};

export const isResultFromCurrentSession = (result: unknown): boolean =>
    !!result && typeof result === 'object' && isAuthSessionCurrent((result as any)[AUTH_SESSION_MARK_KEY]);
